/**
 * Chat interface component
 */
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { AuthContext } from '../../WebApp';

const MEDIA_COSTS = { message: 1, photo: 19, video: 99 };

const MEDIA_BASE =
  process.env.NODE_ENV === 'production'
    ? 'https://hotbump.online'
    : 'http://localhost:8010';

const resolveMediaUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  return `${MEDIA_BASE}${url}`;
};

const hashFromId = (id) => {
  let hash = 0;
  for (let i = 0; i < id.length; i += 1) {
    hash = (hash * 31 + id.charCodeAt(i)) & 0xffffffff;
  }
  return Math.abs(hash);
};

const generateStableLikes = (id) => {
  const base = 142;
  const range = 899 - 142 + 1;
  return base + (hashFromId(id) % range);
};

const generateStableMessages = (id) => {
  const base = 9300;
  const range = 7500;
  return base + (hashFromId(`${id}_msg`) % range);
};

const safeParseJSON = (value, fallback) => {
  try {
    return value ? JSON.parse(value) : fallback;
  } catch (error) {
    return fallback;
  }
};

const getRecentChatsKey = (user) => `hotbump_recent_chats_${user?.id || user?.email || 'guest'}`;
const getEngagementKey = (user) => `hotbump_engagement_${user?.id || user?.email || 'guest'}`;

const parseStatValue = (raw) => {
  if (raw === null || raw === undefined) return 0;
  if (typeof raw === 'number') return raw;
  const cleaned = String(raw).toLowerCase();
  if (cleaned.includes('k')) {
    const numeric = parseFloat(cleaned.replace('k', '').trim());
    return Number.isNaN(numeric) ? 0 : Math.round(numeric * 1000);
  }
  const numeric = parseFloat(cleaned.replace(/[^0-9.]/g, ''));
  return Number.isNaN(numeric) ? 0 : Math.round(numeric);
};

const formatStat = (value) => {
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}k`;
  }
  return String(value);
};

/**
 * Форматирует текст сообщения, выделяя действия курсивом
 * Текст между *действие* будет отображаться курсивом
 * @param {string} text - исходный текст
 * @returns {Array} массив React элементов для рендеринга
 */
const formatMessageText = (text) => {
  if (!text) return null;
  
  // Регулярное выражение для поиска текста между звездочками
  // *действие* или *действие с пробелами*
  const parts = [];
  let lastIndex = 0;
  const regex = /\*([^*]+)\*/g;
  let match;
  
  while ((match = regex.exec(text)) !== null) {
    // Добавляем текст до действия
    if (match.index > lastIndex) {
      const beforeText = text.substring(lastIndex, match.index);
      if (beforeText) {
        parts.push({ type: 'text', content: beforeText });
      }
    }
    
    // Добавляем действие (курсив)
    parts.push({ type: 'action', content: match[1] });
    lastIndex = regex.lastIndex;
  }
  
  // Добавляем оставшийся текст после последнего действия
  if (lastIndex < text.length) {
    const remainingText = text.substring(lastIndex);
    if (remainingText) {
      parts.push({ type: 'text', content: remainingText });
    }
  }
  
  // Если не было найдено действий, возвращаем весь текст как обычный
  if (parts.length === 0) {
    parts.push({ type: 'text', content: text });
  }
  
  return parts;
};

function Chat() {
  const { user, language, updateUser } = React.useContext(AuthContext);
  const { characterId } = useParams();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const generationMenuRef = useRef(null);
  const messageInputRef = useRef(null);

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [typingMessageId, setTypingMessageId] = useState(null);
  const typingQueueRef = useRef([]);
  const typingAnimationFrameRef = useRef(null);
  const isProcessingRef = useRef(false);
  const [character, setCharacter] = useState(null);
  const [characterLoading, setCharacterLoading] = useState(true);
  const [sidebarCharacters, setSidebarCharacters] = useState([]);
  const [sidebarLoading, setSidebarLoading] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [recentChatIds, setRecentChatIds] = useState([]);
  const [media, setMedia] = useState({ images: [], videos: [] });
  const [mediaLoading, setMediaLoading] = useState(true);
  const [activeMedia, setActiveMedia] = useState(null);
  const [engagement, setEngagement] = useState({ likes: 0, messages: 0, liked: false });
  const [photoPrompt, setPhotoPrompt] = useState('');
  const [videoPrompt, setVideoPrompt] = useState('');
  const [photoGenerating, setPhotoGenerating] = useState(false);
  const [videoGenerating, setVideoGenerating] = useState(false);
  const [generationMessage, setGenerationMessage] = useState('');
  const [generationError, setGenerationError] = useState('');
  const [generationMenuOpen, setGenerationMenuOpen] = useState(false);
  const [generationMode, setGenerationMode] = useState(null);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryTab, setGalleryTab] = useState('images');
  const [mediaCollapsed, setMediaCollapsed] = useState(false);
  const hasTokens = typeof user?.token_balance === 'number' && user.token_balance >= 1;
  const [selectedVideoImage, setSelectedVideoImage] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentsCount, setCommentsCount] = useState(0);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentsError, setCommentsError] = useState('');
  const [commentInput, setCommentInput] = useState('');
  const [typingTick, setTypingTick] = useState(0); // триггер перезапуска эффекта печати при новых чанках
  const [resetting, setResetting] = useState(false);
  const [commentPosting, setCommentPosting] = useState(false);
  const [commentsLoaded, setCommentsLoaded] = useState(false);
  const [groupChats, setGroupChats] = useState([]);
  const [groupLoading, setGroupLoading] = useState(true);
  const isGroupChat = characterId && characterId.startsWith('group-');
  const groupId = isGroupChat ? characterId.replace('group-', '') : null;
  const [groupData, setGroupData] = useState(null);
  const [groupDataLoading, setGroupDataLoading] = useState(true);
  const [groupDataError, setGroupDataError] = useState('');
  const [groupInput, setGroupInput] = useState('');
  const [groupSending, setGroupSending] = useState(false);
  const [groupForcing, setGroupForcing] = useState(false);
  const [rightCollapsed, setRightCollapsed] = useState(false);
  const [photoReference, setPhotoReference] = useState(null);
  const [showPhotoRefPicker, setShowPhotoRefPicker] = useState(false);

  const handleResetContext = async () => {
    if (isGroupChat) return;
    const confirmed = window.confirm(
      language === 'en'
        ? 'Are you sure you want to reset the chat history?'
        : 'Вы уверены, что хотите сбросить историю чата?'
    );
    if (!confirmed) return;
    setResetting(true);
    try {
      await axios.delete(`/web/chat/history/${characterId}`);
      typingQueueRef.current = [];
      setTypingMessageId(null);
      setMessages([]);
      setEngagement((prev) => ({ ...prev, messages: 0 }));
    } catch (error) {
      console.error('Failed to reset chat history:', error);
    } finally {
      setResetting(false);
    }
  };

  const loadMedia = React.useCallback(async () => {
    setMediaLoading(true);
    try {
      const response = await axios.get(`/web/characters/${characterId}/media`);
      setMedia(response.data.media || { images: [], videos: [] });
    } catch (error) {
      console.error('Failed to load media', error);
      setMedia({ images: character?.gallery || [], videos: [] });
    } finally {
      setMediaLoading(false);
    }
  }, [characterId, character]);

  const loadHistory = React.useCallback(async () => {
    if (!user) {
      // For unregistered users, load only the greeting message
      try {
        console.log('Loading greeting for character:', characterId, 'language:', language);
        const response = await axios.get(`/web/characters/${characterId}/greeting?language=${language}`);
        console.log('Greeting response:', response.data);
        if (response.data.greeting) {
          setMessages([{ role: 'assistant', content: response.data.greeting }]);
        }
      } catch (error) {
        console.error('Failed to load greeting:', error);
        setMessages([]);
      }
      return;
    }
    try {
      const response = await axios.get(`/web/chat/history/${characterId}`, {
        params: { lang: language }
      });
      setMessages(response.data.history || []);
    } catch (error) {
      console.error('Failed to load history:', error);
    }
  }, [user, characterId, language]);

  const loadComments = React.useCallback(async () => {
    setCommentsLoading(true);
    setCommentsError('');
    try {
      const response = await axios.get(`/web/characters/${characterId}/comments`);
      const list = response.data?.comments || [];
      setComments(list);
      setCommentsCount(response.data?.count ?? list.length);
      setCommentsLoaded(true);
    } catch (error) {
      const detail =
        error?.response?.data?.detail ||
        (language === 'en' ? 'Failed to load comments' : 'Не удалось загрузить комментарии');
      setCommentsError(detail);
    } finally {
      setCommentsLoading(false);
    }
  }, [characterId, language]);

  const loadGroupChat = React.useCallback(async () => {
    if (!isGroupChat || !groupId) return;
    setGroupDataLoading(true);
    setGroupDataError('');
    try {
      const response = await axios.get(`/web/group-chats/${groupId}`);
      setGroupData(response.data);
    } catch (error) {
      const detail = error?.response?.data?.detail || (language === 'en' ? 'Failed to load chat' : 'Не удалось загрузить чат');
      setGroupDataError(detail);
    } finally {
      setGroupDataLoading(false);
    }
  }, [isGroupChat, groupId, language]);

  const handleResetChat = async () => {
    if (!user || !characterId) return;
    const confirmed = window.confirm(
      language === 'en'
        ? 'Start over? The entire conversation with this model will be cleared and she will send her first message again.'
        : 'Начать сначала? Вся переписка с этой моделью будет удалена, и она снова отправит своё первое сообщение.'
    );
    if (!confirmed) return;

    try {
      await axios.delete(`/web/chat/history/${characterId}`);
      await loadHistory();
    } catch (error) {
      console.error('Failed to reset chat history:', error);
    }
  };

  const normalizeCharacter = React.useCallback((data) => {
    if (!data) return null;

    const personality = (data.personality && typeof data.personality === 'object')
      ? data.personality
      : {};
    const appearance = (data.appearance && typeof data.appearance === 'object')
      ? data.appearance
      : {};

    let gallery = [];
    if (Array.isArray(personality.gallery) && personality.gallery.length) {
      gallery = [...personality.gallery];
    } else if (Array.isArray(data.gallery) && data.gallery.length) {
      gallery = [...data.gallery];
    } else if (Array.isArray(personality.photos) && personality.photos.length) {
      gallery = [...personality.photos];
    }

    const baseImage = data.reference_image_url || data.avatar_url || data.image_url || '/avatars/default.png';
    if (gallery.length === 0 && baseImage) {
      gallery = [baseImage];
    }

    const descriptionRu = personality.description || data.description || '';
    const descriptionEn = personality.description_en || data.description_en || '';

    return {
      id: data.id,
      name: data.name,
      ownerId: data.user_id,
      age: appearance.age || appearance.age_range || data.age || '—',
      description: language === 'en'
        ? (descriptionEn || descriptionRu)
        : (descriptionRu || descriptionEn),
      likes: data.id && data.id.startsWith('char_') ? 0 : parseStatValue(data.likes ?? generateStableLikes(data.id)),
      messages: parseStatValue(data.comments ?? generateStableMessages(data.id)),
      categories: data.categories || personality.categories || [],
      gallery,
      mainImage: baseImage,
      avatar: data.avatar_url || baseImage,
      referenceImage: data.reference_image_url || baseImage,
      tagline: personality.tagline || personality.short_description || '',
      hobbies: Array.isArray(personality.hobbies) ? personality.hobbies : (Array.isArray(data.hobbies) ? data.hobbies : []),
      traits: Array.isArray(personality.traits) ? personality.traits : [],
      source: data.source || 'legacy'
    };
  }, [language]);

  const recordRecentChat = React.useCallback((idToStore) => {
    if (!user || !idToStore) return;
    const key = getRecentChatsKey(user);
    const stored = safeParseJSON(localStorage.getItem(key), []);
    const updated = [idToStore, ...stored.filter((id) => id !== idToStore)].slice(0, 12);
    localStorage.setItem(key, JSON.stringify(updated));
    setRecentChatIds(updated);
  }, [user]);

  const persistEngagement = React.useCallback((stats) => {
    if (!user || !characterId) return;
    const key = getEngagementKey(user);
    const stored = safeParseJSON(localStorage.getItem(key), {});
    stored[characterId] = stats;
    localStorage.setItem(key, JSON.stringify(stored));
  }, [user, characterId]);

  useEffect(() => {
    if (!user) return;
    const key = getRecentChatsKey(user);
    const stored = safeParseJSON(localStorage.getItem(key), []);
    setRecentChatIds(stored);
  }, [user]);

  useEffect(() => {
    if (isGroupChat) return;
    if (user && characterId) {
      recordRecentChat(characterId);
    }
  }, [user, characterId, recordRecentChat, isGroupChat]);

  useEffect(() => {
    if (isGroupChat) return;
    setComments([]);
    setCommentsCount(0);
    setCommentsLoaded(false);
    setCommentInput('');
    setCommentsError('');
    const first = media.images?.[0] || character?.avatar || character?.mainImage || null;
    setPhotoReference(first);
    setShowPhotoRefPicker(false);
  }, [characterId, isGroupChat, media.images, character]);

  useEffect(() => {
    const loadGroups = async () => {
      if (!user) {
        setGroupChats([]);
        setGroupLoading(false);
        return;
      }
      setGroupLoading(true);
      try {
        const response = await axios.get('/web/group-chats');
        setGroupChats(response.data?.chats || []);
      } catch (error) {
        setGroupChats([]);
      } finally {
        setGroupLoading(false);
      }
    };
    loadGroups();
  }, [user]);

  useEffect(() => {
    if (isGroupChat) {
      loadGroupChat();
    }
  }, [isGroupChat, loadGroupChat, characterId]);

  useEffect(() => {
    if (isGroupChat) return;
    if (!photoReference) {
      const first = media.images?.[0] || character?.avatar || character?.mainImage || null;
      setPhotoReference(first);
    }
  }, [media.images, character, photoReference, isGroupChat]);

  useEffect(() => {
    if (!generationMenuOpen) return undefined;
    const handleClickOutside = (event) => {
      if (generationMenuRef.current && !generationMenuRef.current.contains(event.target)) {
        setGenerationMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [generationMenuOpen]);

  useEffect(() => {
    // Collapse sidebars by default on mobile
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      setSidebarCollapsed(true);
      setMediaCollapsed(true);
    }
    if (!isGroupChat) {
      loadHistory();
    }
  }, [loadHistory, isGroupChat]);

  useEffect(() => {
    let cancelled = false;

    const fetchSidebar = async () => {
      if (!recentChatIds.length) {
        setSidebarCharacters([]);
        setSidebarLoading(false);
        return;
      }
      setSidebarLoading(true);
      try {
        const response = await axios.get('/web/characters/summaries', {
          params: { ids: recentChatIds.join(','), language }
        });
        if (!cancelled) {
          const summaries = response.data.characters || [];
          const ordered = recentChatIds
            .map((id) => summaries.find((item) => item.id === id))
            .filter(Boolean);
          setSidebarCharacters(ordered);
        }
      } catch (error) {
        console.error('Failed to load summaries, fallback to explore list', error);
        if (!cancelled) {
          const fallback = await axios.get('/explore/characters', { params: { limit: 12 } }).catch(() => ({ data: { characters: [] } }));
          setSidebarCharacters(fallback.data.characters || []);
        }
      } finally {
        if (!cancelled) {
          setSidebarLoading(false);
        }
      }
    };

    fetchSidebar();
    return () => {
      cancelled = true;
    };
  }, [recentChatIds]);

  useEffect(() => {
    if (isGroupChat) return undefined;
    let isMounted = true;

    const loadFallbackCharacter = async () => {
      try {
        const response = await axios.get('/explore/characters', { params: { limit: 80 } });
        const fallback = (response.data.characters || []).find((char) => char.id === characterId);
        if (fallback && isMounted) {
          setCharacter(normalizeCharacter(fallback));
        }
      } catch (fallbackError) {
        console.error('Failed to load fallback character:', fallbackError);
      }
    };

    const loadCharacterDetails = async () => {
      setCharacterLoading(true);
      try {
        const response = await axios.get(`/web/characters/${characterId}`);
        if (isMounted) {
          setCharacter(normalizeCharacter(response.data));
        }
      } catch (error) {
        if (error.response?.status === 404) {
          await loadFallbackCharacter();
        } else {
          console.error('Failed to load character details:', error);
        }
      } finally {
        if (isMounted) {
          setCharacterLoading(false);
        }
      }
    };

    loadCharacterDetails();

    return () => {
      isMounted = false;
    };
  }, [characterId, normalizeCharacter, isGroupChat]);

  useEffect(() => {
    if (isGroupChat) return;
    loadMedia();
  }, [loadMedia, isGroupChat]);

  useEffect(() => {
    if (isGroupChat) return;
    if (galleryOpen && galleryTab === 'comments' && !commentsLoaded) {
      loadComments();
    }
  }, [galleryOpen, galleryTab, commentsLoaded, loadComments, isGroupChat]);

  useEffect(() => {
    if (isGroupChat) return;
    if (!user || !character) return;
    
    // Load likes from server
    const loadLikes = async () => {
      try {
        const response = await axios.get(`/web/characters/${characterId}/likes`);
    const key = getEngagementKey(user);
    const stored = safeParseJSON(localStorage.getItem(key), {});
        
        const isUserCreated = character.id && character.id.startsWith('char_');
        const likesValue = isUserCreated ? response.data.likes : (response.data.likes || character.likes || 0);
        
    const existing = stored[characterId];
    if (existing) {
          // Update with server data
          existing.likes = likesValue;
          existing.liked = response.data.liked;
          stored[characterId] = existing;
          localStorage.setItem(key, JSON.stringify(stored));
      setEngagement(existing);
        } else {
          const defaults = {
            likes: likesValue,
            messages: character.messages || 0,
            liked: response.data.liked
          };
          setEngagement(defaults);
          stored[characterId] = defaults;
          localStorage.setItem(key, JSON.stringify(stored));
    }
      } catch (error) {
        console.error('Failed to load likes:', error);
        // Fallback to local storage
        const key = getEngagementKey(user);
        const stored = safeParseJSON(localStorage.getItem(key), {});
        const existing = stored[characterId];
        if (existing) {
          setEngagement(existing);
        } else {
          const isUserCreated = character.id && character.id.startsWith('char_');
          const likesValue = isUserCreated ? 0 : (character.likes || 0);
    const defaults = {
            likes: likesValue,
      messages: character.messages || 0,
      liked: false
    };
    setEngagement(defaults);
    stored[characterId] = defaults;
    localStorage.setItem(key, JSON.stringify(stored));
        }
      }
    };
    
    loadLikes();
  }, [user, character, characterId, isGroupChat]);

  useEffect(() => {
    if (isGroupChat) return undefined;
    // Улучшенная прокрутка для мобильных устройств
    const scrollToBottom = () => {
      if (messagesEndRef.current) {
        // Для мобильных используем более агрессивную прокрутку
        const isMobile = window.innerWidth < 768;
        if (isMobile) {
          // На мобильных прокручиваем контейнер сообщений, а не элемент
          const messagesContainer = messagesEndRef.current.closest('.messages-container');
          if (messagesContainer) {
            messagesContainer.scrollTop = messagesContainer.scrollHeight;
          } else {
            messagesEndRef.current.scrollIntoView({ behavior: 'auto', block: 'end' });
          }
        } else {
          messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
      }
    };
    
    // Небольшая задержка для корректной прокрутки после рендера
    const timeoutId = setTimeout(scrollToBottom, 100);
    return () => clearTimeout(timeoutId);
  }, [messages, isGroupChat]);

  // Эффект печатания с очередью символов
  useEffect(() => {
    if (isGroupChat) return undefined;
    if (typingQueueRef.current.length === 0 || !typingMessageId) {
      if (typingAnimationFrameRef.current) {
        clearTimeout(typingAnimationFrameRef.current);
        typingAnimationFrameRef.current = null;
      }
      isProcessingRef.current = false;
      return;
    }

    const processQueue = () => {
      if (isProcessingRef.current) return; // Предотвращаем параллельную обработку
      if (typingQueueRef.current.length === 0 || !typingMessageId) {
        setTypingMessageId(null);
        typingAnimationFrameRef.current = null;
        isProcessingRef.current = false;
        return;
      }

      isProcessingRef.current = true;
      
      // Обрабатываем по одному символу за раз для плавного эффекта печатания
      if (typingQueueRef.current.length > 0) {
        const char = typingQueueRef.current.shift();
        
        setMessages((prev) => {
          const updated = [...prev];
          const msgIndex = updated.findIndex((m) => m.id === typingMessageId);
          if (msgIndex >= 0) {
            updated[msgIndex] = {
              ...updated[msgIndex],
              content: (updated[msgIndex].content || '') + char
            };
          }
          return updated;
        });

        // Прокрутка во время печатания (реже, чтобы не нагружать)
        if (Math.random() < 0.1) { // Прокрутка в 10% случаев
          setTimeout(() => {
            const messagesContainer = document.querySelector('.messages-container');
            if (messagesContainer && messagesEndRef.current) {
              messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
            }
          }, 0);
        }
      }

      isProcessingRef.current = false;
      
      // Продолжаем обработку очереди с задержкой для эффекта печатания
      if (typingQueueRef.current.length > 0 && typingMessageId) {
        typingAnimationFrameRef.current = setTimeout(() => {
          processQueue();
        }, 10); // Задержка между символами (ускорили печать)
      } else {
        setTypingMessageId(null);
        typingAnimationFrameRef.current = null;
        isProcessingRef.current = false;
      }
    };

    if (!isProcessingRef.current) {
      typingAnimationFrameRef.current = setTimeout(processQueue, 20);
    }

    return () => {
      if (typingAnimationFrameRef.current) {
        clearTimeout(typingAnimationFrameRef.current);
      }
      isProcessingRef.current = false;
    };
  }, [typingMessageId, typingTick, isGroupChat]);

  const sendMessage = async (e) => {
    e.preventDefault();
    // Block unregistered users from sending messages
    if (!user) {
      navigate('/web/login');
      return;
    }
    if (!input.trim() || loading) return;

    const userMessage = input;
    setInput('');
    // Reset textarea height after sending
    if (messageInputRef.current) {
      messageInputRef.current.style.height = '48px';
    }
    setLoading(true);

    // Очищаем очередь печатания
    typingQueueRef.current = [];
    if (typingAnimationFrameRef.current) {
      clearTimeout(typingAnimationFrameRef.current);
      typingAnimationFrameRef.current = null;
    }

    // Сохраняем ID сообщения пользователя для отслеживания (объявляем вне try, чтобы был доступен в catch)
    const userMessageId = `user_msg_${Date.now()}_${Math.random()}`;
    const assistantMessageId = `msg_${Date.now()}_${Math.random()}`;
    
    setMessages((prev) => [...prev, { id: userMessageId, role: 'user', content: userMessage }]);
    
    // Прокрутка сразу после отправки сообщения пользователем
    setTimeout(() => {
      const messagesContainer = document.querySelector('.messages-container');
      if (messagesContainer && messagesEndRef.current) {
        messagesContainer.scrollTop = messagesContainer.scrollHeight;
      }
    }, 50);

    // Создаем пустое сообщение для ответа модели
    setMessages((prev) => [...prev, { id: assistantMessageId, role: 'assistant', content: '' }]);
    setTypingMessageId(assistantMessageId);

    try {
      let fullResponse = '';
      let tokensRemaining = user.token_balance;

      // Get token from localStorage
      const token = localStorage.getItem('auth_token');
      const headers = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      console.log('Starting streaming request to:', characterId, 'message:', userMessage.substring(0, 50) + '...');
      await fetchEventSource('/api/web/chat/stream', {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          character_id: characterId,
          message: userMessage
        }),
        onmessage(event) {
          console.log('Received streaming data:', event.data.substring(0, 100) + '...');
          if (event.data) {
            // Проверяем на ошибки
            if (event.data.startsWith('ERROR:')) {
              const errorMsg = event.data.substring(6);
              // Если недостаточно токенов, перенаправляем на promo
              if (errorMsg.includes('Insufficient tokens')) {
                setTypingMessageId(null);
                setLoading(false);
                navigate('/web/promo');
                return;
              }
              setMessages((prev) => {
                const updated = [...prev];
                const msgIndex = updated.findIndex((m) => m.id === assistantMessageId);
                if (msgIndex >= 0) {
                  updated[msgIndex] = {
                    ...updated[msgIndex],
                    content: errorMsg
                  };
                }
                return updated;
              });
              setTypingMessageId(null);
              setLoading(false);
              return;
            }
            fullResponse += event.data;
            // Добавляем символы в очередь для эффекта печатания (по одному символу)
            for (const char of event.data) {
              typingQueueRef.current.push(char);
            }
            // Пингуем эффект печатания, чтобы он стартовал/продолжился
            setTypingTick(Date.now());
          }
        },
        onerror(err) {
          console.error('Streaming error:', err);
          console.error('Full response so far:', fullResponse);
          console.error('Error details:', err.message, err.status, err.statusText);
          typingQueueRef.current = [];
          setMessages((prev) => {
            const updated = [...prev];
            const msgIndex = updated.findIndex((m) => m.id === assistantMessageId);
            if (msgIndex >= 0) {
              updated[msgIndex] = {
                ...updated[msgIndex],
                content: fullResponse || (language === 'en' ? 'Error sending message' : 'Ошибка отправки сообщения')
              };
            }
            return updated;
          });
          setTypingMessageId(null);
          setLoading(false);
        },
        onclose() {
          console.log('Streaming closed, final response:', fullResponse.substring(0, 100) + '...');
          setLoading(false);

          // Дожидаемся, пока очередь печатания опустеет, затем фиксируем счетчики/токены/scroll
          const finalize = () => {
            if (typingQueueRef.current.length > 0) {
              setTimeout(finalize, 80);
              return;
            }
            // Очередь пустая — завершаем печатание и фиксируем итог
            setTypingMessageId(null);

            setEngagement((prev) => {
              const updated = { ...prev, messages: prev.messages + 1 };
              persistEngagement(updated);
              return updated;
            });

            if (updateUser && tokensRemaining !== undefined) {
              updateUser({
                ...user,
                token_balance: tokensRemaining - 1
              });
            }
            
            setTimeout(() => {
              const messagesContainer = document.querySelector('.messages-container');
              if (messagesContainer && messagesEndRef.current) {
                messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
              }
            }, 100);
          };

          setTimeout(finalize, 300); // финализация, даже если очередь вдруг зависнет
          finalize();
        }
      });
    } catch (error) {
      console.error('Failed to send message:', error);
      const insufficientTokens = error.response?.status === 402;
      const errorText = insufficientTokens
        ? (language === 'en'
          ? 'Not enough tokens. Activate a promo code to continue.'
          : 'Недостаточно токенов. Активируйте промокод, чтобы продолжить.')
        : (language === 'en' ? 'Error sending message' : 'Ошибка отправки сообщения');

      // Убеждаемся, что сообщение пользователя не удалено
      setMessages((prev) => {
        // Проверяем, есть ли сообщение пользователя
        const hasUserMessage = prev.some(m => m.id === userMessageId || (m.role === 'user' && m.content === userMessage));
        const updated = hasUserMessage ? [...prev] : [...prev, { id: userMessageId, role: 'user', content: userMessage }];
        
        const msgIndex = updated.findIndex((m) => m.id === assistantMessageId);
        if (msgIndex >= 0) {
          updated[msgIndex] = {
            ...updated[msgIndex],
            content: errorText
          };
        } else {
          updated.push({ id: assistantMessageId, role: 'assistant', content: errorText });
        }
        return updated;
      });
      setTypingMessageId(null);
      setLoading(false);
    }
  };

  const handleCommentSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!user) {
      navigate('/web/login');
      return;
    }
    const text = commentInput.trim();
    if (!text) {
      setCommentsError(language === 'en' ? 'Comment cannot be empty' : 'Комментарий не может быть пустым');
      return;
    }
    if (text.length > 150) {
      setCommentsError(language === 'en' ? 'Max 150 characters' : 'Максимум 150 символов');
      return;
    }
    if (commentPosting) return;
    setCommentPosting(true);
    setCommentsError('');
    try {
      const response = await axios.post(`/web/characters/${characterId}/comments`, { content: text });
      if (response.data?.comment) {
        setComments((prev) => [response.data.comment, ...(prev || [])]);
        setCommentsCount((prev) => (typeof prev === 'number' ? prev + 1 : 1));
      }
      if (typeof response.data?.token_balance === 'number' && updateUser) {
        updateUser({ ...(user || {}), token_balance: response.data.token_balance });
      }
      setCommentInput('');
    } catch (error) {
      const detail =
        error?.response?.data?.detail ||
        (language === 'en' ? 'Failed to send comment' : 'Не удалось отправить комментарий');
      setCommentsError(detail);
    } finally {
      setCommentPosting(false);
    }
  };

  const handleGroupSend = async (e) => {
    if (e) e.preventDefault();
    if (!groupInput.trim() || !groupData) return;
    const isUserTurn = groupData.current_turn && groupData.participants?.some(
      (p) => p.key === groupData.current_turn && p.type === 'user' && p.user_id === user?.id
    );
    if (!isUserTurn) {
      setGroupDataError(language === 'en' ? "It's not your turn" : 'Сейчас не ваш ход');
      return;
    }
    if (user && typeof user.token_balance === 'number' && user.token_balance < 1) {
      setGroupDataError(language === 'en' ? 'Not enough tokens' : 'Недостаточно токенов');
      return;
    }
    setGroupSending(true);
    setGroupDataError('');
    try {
      const response = await axios.post(`/web/group-chats/${groupId}/message`, { content: groupInput.trim() });
      if (response?.data) {
        setGroupData(response.data);
        if (typeof response.data.token_balance === 'number' && updateUser) {
          updateUser({ ...(user || {}), token_balance: response.data.token_balance });
        }
      }
      setGroupInput('');
    } catch (error) {
      const detail = error?.response?.data?.detail || (language === 'en' ? 'Failed to send message' : 'Не удалось отправить сообщение');
      setGroupDataError(detail);
    } finally {
      setGroupSending(false);
    }
  };

  const handleForceGroup = async (participantKey) => {
    if (!participantKey || !groupData) return;
    setGroupForcing(true);
    setGroupDataError('');
    try {
      const response = await axios.post(`/web/group-chats/${groupId}/force-turn`, { participant_key: participantKey });
      if (response?.data) {
        setGroupData(response.data);
        if (typeof response.data.token_balance === 'number' && updateUser) {
          updateUser({ ...(user || {}), token_balance: response.data.token_balance });
        }
      }
    } catch (error) {
      const detail = error?.response?.data?.detail || (language === 'en' ? 'Failed to trigger model' : 'Не удалось вызвать модель');
      setGroupDataError(detail);
    } finally {
      setGroupForcing(false);
    }
  };

  const handleLikeToggle = async () => {
    try {
      const response = await axios.post(`/web/characters/${characterId}/like`);
      const newLiked = response.data.liked;
      const newLikes = newLiked ? engagement.likes + 1 : Math.max(0, engagement.likes - 1);
      
    setEngagement((prev) => {
      const updated = {
        ...prev,
          liked: newLiked,
          likes: newLikes
      };
      persistEngagement(updated);
      return updated;
    });
    } catch (error) {
      console.error('Failed to toggle like:', error);
    }
  };

  const openGenerationPanel = (mode) => {
    setGenerationMode(mode);
    setGenerationMenuOpen(false);
    setGenerationError('');
    setGenerationMessage('');
    if (mode === 'video') {
      // по умолчанию берём первое фото из галереи как источник видео
      setSelectedVideoImage((media.images && media.images.length > 0) ? media.images[0] : null);
    }
  };

  const closeGenerationPanel = () => {
    setGenerationMode(null);
    setGenerationError('');
    setSelectedVideoImage(null);
  };

  const handlePhotoGeneration = async () => {
    const promptText = photoPrompt.trim();
    if (!promptText) {
      setGenerationError(language === 'en' ? 'Please describe the photo you want.' : 'Опишите, какое фото вы хотите получить.');
      return;
    }
    if (!photoReference) {
      setGenerationError(language === 'en' ? 'Choose a reference image.' : 'Выберите референс.');
      return;
    }
    setGenerationError('');
    setGenerationMessage('');
    setPhotoGenerating(true);
    try {
      const response = await axios.post(`/web/characters/${characterId}/generate_photo`, {
        prompt: promptText,
        image_url: photoReference
      });
      const newUrl = response.data.image_url;
      if (newUrl) {
        setGenerationMessage(language === 'en' ? 'Photo generated!' : 'Фото готово!');
        setPhotoPrompt('');
        setGenerationMode(null);
        setGenerationMenuOpen(false);
        await Promise.all([loadMedia(), loadHistory()]);
        // Добавляем сообщение с фото в чат сразу
        setMessages((prev) => [
          ...prev,
          {
            id: `gen-photo-${Date.now()}`,
            role: 'assistant',
            content: generationMessage || '',
            media: { type: 'image', url: newUrl },
            meta: { generated: true, prompt: promptText }
          }
        ]);
      }
      if (updateUser && typeof response.data.token_balance === 'number') {
        updateUser({ ...user, token_balance: response.data.token_balance });
      }
    } catch (err) {
      setGenerationError(err.response?.data?.detail || (language === 'en' ? 'Failed to generate photo.' : 'Не удалось сгенерировать фото.'));
    } finally {
      setPhotoGenerating(false);
      setTimeout(() => setGenerationMessage(''), 5000);
    }
  };

  const handleVideoGeneration = async () => {
    const promptText = videoPrompt.trim();
    if (!promptText) {
      setGenerationError(language === 'en' ? 'Please describe the video idea.' : 'Опишите идею видео.');
      return;
    }
    if (!selectedVideoImage) {
      setGenerationError(language === 'en'
        ? 'Choose a base photo for the video.'
        : 'Выберите фото, из которого делать видео.');
      return;
    }
    setGenerationError('');
    setGenerationMessage('');
    setVideoGenerating(true);
    try {
      const response = await axios.post(`/web/characters/${characterId}/generate_video`, {
        prompt: promptText,
        image_url: selectedVideoImage
      });
      const newUrl = response.data.video_url;
      if (newUrl) {
        setGenerationMessage(language === 'en' ? 'Video generated!' : 'Видео готово!');
        setVideoPrompt('');
        setGenerationMode(null);
        setGenerationMenuOpen(false);
        await Promise.all([loadMedia(), loadHistory()]);
        setMessages((prev) => [
          ...prev,
          {
            id: `gen-video-${Date.now()}`,
            role: 'assistant',
            content: generationMessage || '',
            media: { type: 'video', url: newUrl },
            meta: { generated: true, prompt: promptText }
          }
        ]);
      }
      if (updateUser && typeof response.data.token_balance === 'number') {
        updateUser({ ...user, token_balance: response.data.token_balance });
      }
    } catch (err) {
      setGenerationError(err.response?.data?.detail || (language === 'en' ? 'Failed to generate video.' : 'Не удалось сгенерировать видео.'));
    } finally {
      setVideoGenerating(false);
      setTimeout(() => setGenerationMessage(''), 5000);
    }
  };

  const renderMessageBubble = (msg, idx) => {
    const mediaType = msg.media?.type;
    const mediaUrl = msg.media?.url;
    const isGenerated = Boolean(msg.meta?.generated);
    const promptText = msg.meta?.prompt || msg.content;
    const generatedLabel = language === 'en'
      ? (mediaType === 'video' ? 'Generated video' : 'Generated photo')
      : (mediaType === 'video' ? 'Сгенерированное видео' : 'Сгенерированное фото');

    return (
    <div
        key={`${idx}-${msg.role}-${msg.timestamp || msg.id || ''}`}
      className={`flex ${msg.role === 'user' ? 'justify-end pr-[15%]' : 'justify-start pl-[15%]'}`}
    >
        <div
            className={`max-w-[85%] lg:max-w-2xl px-4 py-3 rounded-2xl text-base sm:text-lg backdrop-blur space-y-3 ${
          msg.role === 'user'
            ? 'bg-burgundy-600/80 text-white shadow-[0_0_20px_rgba(190,18,60,0.35)]'
            : 'bg-black/40 text-gray-100 border border-white/10'
        }`}
      >
          {!isGenerated && (
            <p className="whitespace-pre-line leading-relaxed">
              {msg.content ? (
                formatMessageText(msg.content).map((part, partIdx) => {
                  if (part.type === 'action') {
                    return (
                      <em key={partIdx} className="italic text-gray-200">
                        {part.content}
                      </em>
                    );
                  }
                  // Сохраняем переносы строк в обычном тексте
                  return (
                    <span key={partIdx}>
                      {part.content.split('\n').map((line, lineIdx, lines) => (
                        <React.Fragment key={lineIdx}>
                          {line}
                          {lineIdx < lines.length - 1 && <br />}
                        </React.Fragment>
                      ))}
                    </span>
                  );
                })
              ) : (
                <span className="inline-block text-gray-400 animate-pulse ml-1">...</span>
              )}
            </p>
          )}
          {isGenerated && (
            <div className="space-y-1">
              <div className="text-[11px] uppercase tracking-[0.3em] text-emerald-300">
                {generatedLabel}
              </div>
              {promptText && (
                <p className="text-xs sm:text-sm text-gray-300 italic whitespace-pre-line">
                  {promptText}
                </p>
              )}
            </div>
          )}
          {mediaType === 'image' && mediaUrl && (
            <button
              type="button"
              onClick={() => {
                // Находим индекс изображения в медиа для открытия в просмотре
                const imageIndex = media.images.findIndex(img => resolveMediaUrl(img) === resolveMediaUrl(mediaUrl));
                if (imageIndex >= 0) {
                  openMedia('images', imageIndex);
                } else {
                  // Если не найдено в галерее, открываем напрямую
                  setActiveMedia({
                    collection: 'images',
                    index: 0,
                    type: 'image',
                    directUrl: mediaUrl
                  });
                }
              }}
              className="block w-full rounded-2xl border border-white/10 overflow-hidden hover:border-white/30 transition cursor-pointer"
            >
              <img
                src={resolveMediaUrl(mediaUrl)}
                alt="Generated media"
                className="w-full max-h-[600px] object-cover rounded-2xl"
              />
            </button>
          )}
          {mediaType === 'video' && mediaUrl && (
            <video
              src={resolveMediaUrl(mediaUrl)}
              controls
            className="rounded-2xl w-full h-auto max-h-[70vh] bg-black"
            style={{ objectFit: 'contain' }}
            />
          )}
      </div>
    </div>
  );
  };

  const openMedia = useCallback((collection, index = 0) => {
    setActiveMedia(() => {
      const items = media[collection] || [];
      if (!items.length) {
        return null;
      }
      const safeIndex = Math.min(Math.max(index, 0), items.length - 1);
      return {
        collection,
        index: safeIndex,
        type: collection === 'videos' ? 'video' : 'image'
      };
    });
  }, [media]);

  const handleMediaNavigation = useCallback((direction) => {
    setActiveMedia((prev) => {
      if (!prev) return prev;
      const items = media[prev.collection] || [];
      if (!items.length) {
        return null;
      }
      const total = items.length;
      const newIndex = (prev.index + direction + total) % total;
      return { ...prev, index: newIndex };
    });
  }, [media]);

  useEffect(() => {
    setActiveMedia((prev) => {
      if (!prev) return prev;
      const items = media[prev.collection] || [];
      if (!items.length) {
        return null;
      }
      const safeIndex = Math.min(prev.index, items.length - 1);
      if (safeIndex !== prev.index) {
        return { ...prev, index: safeIndex };
      }
      return prev;
    });
  }, [media]);

  useEffect(() => {
    if (!activeMedia) return undefined;
    const handleKey = (event) => {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        handleMediaNavigation(1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        handleMediaNavigation(-1);
      } else if (event.key === 'Escape') {
        setActiveMedia(null);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [activeMedia, handleMediaNavigation]);

  const renderSidebar = () => (
    <aside
      className={`flex ${
        sidebarCollapsed ? 'w-16' : 'w-64 xl:w-72'
      } flex-col bg-black/40 backdrop-blur-md border-r border-white/10 transition-all flex-shrink-0`}
    >
      <div className="flex items-center justify-between px-4 py-4 border-b border-white/10">
        {!sidebarCollapsed && (
          <div>
            <div className="text-xs uppercase tracking-wider text-gray-400">
              {language === 'en' ? 'Recent chats' : 'Недавние чаты'}
            </div>
            <div className="text-lg font-semibold">{language === 'en' ? 'History' : 'История'}</div>
          </div>
        )}
        <button
          onClick={() => setSidebarCollapsed((prev) => !prev)}
          className="text-2xl text-white/70 hover:text-white transition"
          aria-label={sidebarCollapsed ? (language === 'en' ? 'Expand chats' : 'Открыть список чатов') : (language === 'en' ? 'Collapse chats' : 'Скрыть список чатов')}
        >
          {sidebarCollapsed ? '›' : '‹'}
        </button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {sidebarLoading ? (
          <div className="text-gray-400 text-sm p-4">
            {language === 'en' ? 'Loading characters...' : 'Загрузка персонажей...'}
          </div>
        ) : sidebarCharacters.length === 0 ? (
          <div className="text-gray-400 text-sm p-4">
            {language === 'en'
              ? 'Chats you open will appear here.'
              : 'Чаты, которые вы открываете, появятся здесь.'}
          </div>
        ) : (
          sidebarCharacters.map((item) => (
            <button
              key={item.id}
              onClick={() => navigate(`/web/chat/${item.id}`)}
              className={`w-full flex items-center gap-3 px-4 py-3 text-left transition ${
                item.id === characterId ? 'bg-white/10' : 'hover:bg-white/5'
              }`}
            >
              <img
                src={item.avatar_url || '/avatars/default.png'}
                alt={item.name}
                className={`rounded-xl object-cover border border-white/10 ${
                  sidebarCollapsed ? 'h-10 w-10' : 'h-12 w-12'
                }`}
              />
              {!sidebarCollapsed && (
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">{item.name}</div>
                </div>
              )}
            </button>
          ))
        )}
        {user && (
          <div className="border-t border-white/10 mt-2 pt-2">
            <div className="text-xs uppercase tracking-wider text-gray-400 px-4 pb-1">
              {language === 'en' ? 'Group chats' : 'Групповые чаты'}
            </div>
            {groupLoading ? (
              <div className="text-gray-400 text-sm p-4">
                {language === 'en' ? 'Loading...' : 'Загрузка...'}
              </div>
            ) : groupChats.length === 0 ? (
              <div className="text-gray-500 text-xs px-4 pb-2">
                {language === 'en' ? 'No group chats yet.' : 'Групповых чатов пока нет.'}
              </div>
            ) : (
              groupChats.map((gc) => (
                <button
                  key={`group-${gc.id}`}
                  onClick={() => navigate(`/web/chat/group-${gc.id}`)}
                  className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-white/5 transition"
                >
                  <div className={`rounded-xl bg-burgundy-700 text-white text-xs font-semibold h-10 w-10 flex items-center justify-center border border-white/10`}>
                    G
                  </div>
                  {!sidebarCollapsed && (
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm truncate">
                        {gc.title || (language === 'en' ? 'Group chat' : 'Групповой чат')}
                      </div>
                      <div className="text-[11px] text-gray-400 truncate">
                        {(gc.participants || []).slice(0, 3).map((p) => p.name).join(', ')}
                      </div>
                    </div>
                  )}
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </aside>
  );

  const mediaGrid = useMemo(() => {
    if (!media.images.length && !media.videos.length) {
      return (
        <div className="text-gray-400 text-sm">
          {mediaLoading
            ? (language === 'en' ? 'Loading media...' : 'Загрузка медиа...')
            : (language === 'en' ? 'No media yet' : 'Медиа отсутствуют')}
        </div>
      );
    }
    return (
      <>
        {media.images.length > 0 && (
          <div className="grid grid-cols-2 gap-x-4 gap-y-6">
            {media.images.slice(0, 6).map((url, idx) => (
              <button
                key={`${url}-${idx}`}
                type="button"
                onClick={() => openMedia('images', idx)}
                className="rounded-2xl overflow-hidden focus:outline-none focus:ring-2 focus:ring-burgundy-500 flex items-center justify-center"
                style={{ aspectRatio: '2 / 3' }}
              >
                <img
                  src={resolveMediaUrl(url)}
                  alt={character?.name || 'Media'}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </button>
            ))}
          </div>
        )}
        {media.videos.length > 0 && (
          <div className="space-y-4 mt-6">
            {media.videos.slice(0, 2).map((url, idx) => (
              <button
                key={`${url}-${idx}`}
                type="button"
                onClick={() => {
                  if (!hasTokens) return;
                  openMedia('videos', idx);
                }}
                disabled={!hasTokens}
                className="w-full rounded-xl overflow-hidden focus:outline-none focus:ring-2 focus:ring-burgundy-500 h-48"
                title={
                  hasTokens
                    ? undefined
                    : (language === 'en'
                      ? 'Top up at least 1 token to watch videos'
                      : 'Пополните баланс хотя бы на 1 токен, чтобы смотреть видео')
                }
              >
                <video
                  src={resolveMediaUrl(url)}
                  className="w-full h-full object-cover"
                  muted
                  loop
                  playsInline
                />
              </button>
            ))}
          </div>
        )}
      </>
    );
  }, [media, mediaLoading, language, character, openMedia]);
  
  // Allow unregistered users to view chat, but they can't send messages or open gallery

  if (!isGroupChat && characterLoading && !character) {
    return (
      <div className="flex items-center justify-center h-[60vh] text-gray-300">
        {language === 'en' ? 'Loading chat...' : 'Загрузка чата...'}
      </div>
    );
  }

  const renderMediaPanel = () => {
    const panelPadding = mediaCollapsed ? 'px-5' : 'pl-16 pr-6';
    const collapseButtonStyle = mediaCollapsed
      ? { left: '50%', transform: 'translateX(-50%)' }
      : { left: '12px' };
    return (
      <aside
        className={`flex ${mediaCollapsed ? 'w-12' : 'w-[18rem] sm:w-[22rem] lg:w-[30rem]'} relative flex-col bg-black/35 backdrop-blur-md border-l border-white/10 transition-all duration-300 overflow-visible flex-shrink-0`}
      >
        <button
          type="button"
          onClick={() => setMediaCollapsed((prev) => !prev)}
          className="absolute top-7 text-3xl text-white/70 hover:text-white transition z-30"
          style={collapseButtonStyle}
          aria-label={mediaCollapsed ? (language === 'en' ? 'Show media panel' : 'Показать медиа') : (language === 'en' ? 'Hide media panel' : 'Скрыть медиа')}
        >
          {mediaCollapsed ? '‹' : '›'}
        </button>
        {!mediaCollapsed && (
        <>
            <div className={`${panelPadding} py-5 border-b border-white/10`}>
              <div className="flex items-center gap-4">
          <img
            src={character?.avatar || '/avatars/default.png'}
            alt={character?.name || 'Character'}
                  className="h-12 w-12 sm:h-16 sm:w-16 rounded-2xl object-cover border border-white/10 flex-shrink-0"
          />
          <div>
            <div className="text-base sm:text-lg uppercase tracking-widest text-gray-400">{language === 'en' ? 'Model' : 'Модель'}</div>
            <div className="text-2xl sm:text-3xl font-bold">{character?.name}</div>
                  {character?.age && character.age !== 'N/A' && character.age !== '—' && (
                    <div className="text-base sm:text-lg text-gray-300">{language === 'en' ? `${character.age} y.o.` : `${character.age} лет`}</div>
                  )}
          </div>
        </div>
      </div>

            <div className={`${panelPadding} py-4 space-y-4 overflow-y-auto`}>
        <div>
          <div className="text-xs uppercase tracking-wider text-gray-400 mb-2">
            {language === 'en' ? 'Statistics' : 'Статистика'}
          </div>
                <div className="rounded-2xl border border-white/10 bg-white/5 p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-gray-400 text-xs uppercase tracking-widest">
                        {language === 'en' ? 'Likes' : 'Лайки'}
                      </div>
                      <div className="text-2xl font-semibold">{formatStat(engagement.likes)}</div>
                    </div>
                    <button
                      type="button"
                      onClick={handleLikeToggle}
                      className="h-12 w-12 rounded-full border border-white/15 bg-black/30 flex items-center justify-center hover:bg-black/50 transition"
                      aria-pressed={engagement.liked}
                    >
                      <span className={`text-2xl ${engagement.liked ? 'text-red-500' : 'text-gray-400'}`}>
                        {engagement.liked ? '❤️' : '♡'}
                      </span>
                    </button>
                  </div>
                </div>

                {!isGroupChat && (
                  <div className="rounded-2xl border border-white/10 bg-white/5 p-4 mt-2">
                    <button
                      type="button"
                      onClick={handleResetContext}
                      disabled={resetting}
                      className="w-full px-3 py-2 text-sm rounded-xl border border-white/10 text-gray-200 hover:bg-white/5 disabled:opacity-50"
                    >
                      {resetting
                        ? (language === 'en' ? 'Resetting...' : 'Сбрасываю...')
                        : (language === 'en' ? 'Reset context' : 'Сброс контекста')}
                    </button>
                  </div>
                )}
        </div>

        {character?.description && (
          <div>
            <div className="text-xs uppercase tracking-wider text-gray-400 mb-2">
              {language === 'en' ? 'About' : 'О модели'}
            </div>
            <p className="text-sm text-gray-200 leading-relaxed">{character.description}</p>
          </div>
        )}

        <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs uppercase tracking-wider text-gray-400">
            {language === 'en' ? 'Media' : 'Медиа'}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      if (!user) {
                        // Redirect to login for unregistered users
                        navigate('/web/login');
                        return;
                      }
                      setGalleryOpen(true);
                    }}
                    disabled={!user}
                    className={`text-[10px] uppercase tracking-widest transition ${
                      user
                        ? 'text-gray-300 hover:text-white'
                        : 'text-gray-500 cursor-not-allowed'
                    }`}
                    title={
                      !user
                        ? (language === 'en'
                          ? 'Please register to open the gallery'
                          : 'Пожалуйста, зарегистрируйтесь, чтобы открыть галерею')
                        : undefined
                    }
                  >
                    {language === 'en' ? 'Open gallery' : 'Открыть галерею'}
                  </button>
          </div>
          {mediaGrid}
        </div>
            </div>
          </>
        )}
      </aside>
    );
  };

  const renderGalleryOverlay = () => {
    if (!galleryOpen) {
      return null;
    }
    const isCommentsTab = galleryTab === 'comments';
    const currentItems = galleryTab === 'images' ? media.images : galleryTab === 'videos' ? media.videos : [];
    return (
      <div className="fixed inset-0 bg-black/85 py-8 px-4 sm:px-10 overflow-y-auto" style={{ zIndex: 3000 }}>
        <div className="max-w-5xl mx-auto bg-black/60 border border-white/10 rounded-3xl p-6 sm:p-8 backdrop-blur-xl shadow-2xl">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="text-xs uppercase tracking-[0.4em] text-gray-400">
                {language === 'en' ? 'Model gallery' : 'Галерея модели'}
              </div>
              <div className="text-2xl font-semibold text-white">{character?.name}</div>
            </div>
          <button
            type="button"
              onClick={() => setGalleryOpen(false)}
              className="text-gray-300 hover:text-white text-2xl"
              aria-label={language === 'en' ? 'Close gallery' : 'Закрыть галерею'}
          >
              ×
            </button>
            </div>

          <div className="flex gap-3 mb-6">
            <button
              type="button"
              onClick={() => setGalleryTab('images')}
              className={`px-4 py-2 rounded-full text-sm font-medium ${
                galleryTab === 'images' ? 'bg-burgundy-600 text-white' : 'bg-white/10 text-gray-300'
              }`}
            >
              {language === 'en' ? 'Photos' : 'Фото'} ({media.images.length})
          </button>
          <button
            type="button"
              onClick={() => setGalleryTab('videos')}
              className={`px-4 py-2 rounded-full text-sm font-medium ${
                galleryTab === 'videos' ? 'bg-burgundy-600 text-white' : 'bg-white/10 text-gray-300'
              }`}
            >
              {language === 'en' ? 'Videos' : 'Видео'} ({media.videos.length})
            </button>
            <button
              type="button"
              onClick={() => setGalleryTab('comments')}
              className={`px-4 py-2 rounded-full text-sm font-medium ${
                isCommentsTab ? 'bg-burgundy-600 text-white' : 'bg-white/10 text-gray-300'
              }`}
            >
              {language === 'en' ? 'Comments' : 'Комментарии'} ({commentsCount})
            </button>
            </div>

          {isCommentsTab ? (
            <div className="space-y-4">
              {commentsError && (
                <div className="text-sm text-red-300 bg-red-900/30 border border-red-600/30 px-3 py-2 rounded-xl">
                  {commentsError}
                </div>
              )}
              {user ? (
                <form onSubmit={handleCommentSubmit} className="space-y-2">
                  <textarea
                    value={commentInput}
                    onChange={(e) => {
                      if (e.target.value.length <= 150) {
                        setCommentInput(e.target.value);
                      }
                    }}
                    maxLength={150}
                    className="w-full rounded-xl bg-black/40 border border-white/10 text-sm text-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-burgundy-500"
                    placeholder={language === 'en' ? 'Leave a comment...' : 'Оставьте комментарий...'}
                    rows={3}
                  />
                  <div className="flex items-center justify-between text-xs text-gray-400">
                    <span>{language === 'en' ? '1 token per comment' : '1 токен за комментарий'}</span>
                    <span>{commentInput.length}/150</span>
                  </div>
                  <div className="flex justify-end">
                    <button
                      type="submit"
                      disabled={commentPosting || commentInput.trim().length === 0 || (user && user.token_balance < 1)}
                      onClick={(e) => {
                        if (user && user.token_balance < 1) {
                          e.preventDefault();
                          navigate('/web/promo');
                          return;
                        }
                      }}
                      className="px-4 py-2 rounded-lg bg-burgundy-600 hover:bg-burgundy-500 text-white text-sm font-semibold disabled:opacity-60"
                    >
                      {commentPosting
                        ? (language === 'en' ? 'Sending...' : 'Отправка...')
                        : (language === 'en' ? 'Send comment' : 'Отправить')}
                    </button>
                  </div>
                </form>
              ) : (
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => navigate('/web/login')}
                    className="px-4 py-2 rounded-lg bg-burgundy-600 hover:bg-burgundy-500 text-white text-sm font-semibold"
                  >
                    {language === 'en' ? 'Login to comment' : 'Войдите, чтобы писать'}
                  </button>
                </div>
              )}

              {commentsLoading ? (
                <div className="text-gray-400 text-center py-6">
                  {language === 'en' ? 'Loading comments...' : 'Загрузка комментариев...'}
                </div>
              ) : comments.length === 0 ? (
                <div className="text-gray-400 text-center py-6">
                  {language === 'en' ? 'No comments yet.' : 'Комментариев пока нет.'}
                </div>
              ) : (
                <div className="space-y-3">
                  {comments.map((item) => (
                    <div key={item.id} className="rounded-2xl border border-white/10 bg-black/30 p-3">
                      <div className="flex items-center justify-between text-sm text-gray-300">
                        <span className="font-semibold">{item.author}</span>
                        {item.created_at && (
                          <span className="text-xs text-gray-400">
                            {new Date(item.created_at).toLocaleString()}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-white mt-2 whitespace-pre-line">{item.content}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : currentItems.length === 0 ? (
            <div className="text-gray-400 text-center py-10">
              {language === 'en' ? 'No media yet.' : 'Медиа пока нет.'}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {currentItems.map((url, idx) => (
                <button
                  key={`${url}-${idx}`}
                  type="button"
                  onClick={() => {
                    openMedia(galleryTab, idx);
                    setGalleryOpen(false);
                  }}
                  className="rounded-2xl overflow-hidden bg-black/40 h-64"
                >
                  {galleryTab === 'images' ? (
                    <img
                      src={resolveMediaUrl(url)}
                      alt="Gallery preview"
                      className="w-full h-full object-contain bg-black"
                    />
                  ) : (
                    <video
                      src={resolveMediaUrl(url)}
                      className="w-full h-full object-contain bg-black"
                      muted
                      loop
                      playsInline
                      controls={hasTokens}
                    />
                  )}
          </button>
              ))}
        </div>
          )}
      </div>
      </div>
  );
  };


  const renderChatArea = () => (
    <section className="flex-1 flex flex-col min-h-0 overflow-hidden" style={{ touchAction: 'pan-y' }}>
      {/* Header hidden per design */}

      <div 
        className="flex-1 overflow-y-auto px-4 sm:px-8 py-6 space-y-4 messages-container" 
        style={{ 
          WebkitOverflowScrolling: 'touch',
          touchAction: 'pan-y',
          overscrollBehavior: 'contain',
          minHeight: 0,
          maxHeight: 'none',
          flex: '1 1 auto'
        }}
      >
        {messages.length === 0 ? (
          <div className="text-center text-gray-300 mt-10">
            {language === 'en'
              ? 'Start the conversation and she will answer instantly.'
              : 'Начните переписку, и она ответит мгновенно.'}
          </div>
        ) : (
          messages.map((msg, idx) => renderMessageBubble(msg, idx))
        )}
        <div ref={messagesEndRef} style={{ height: '1px', minHeight: '1px' }} />
      </div>

      <form onSubmit={sendMessage} className="px-4 sm:px-8 pb-4 sm:pb-6 flex-shrink-0 max-w-6xl mx-auto w-full" style={{ touchAction: 'manipulation' }}>
        <div className="bg-black/50 border border-white/10 rounded-2xl p-3 flex flex-col gap-3 backdrop-blur relative w-full">
          {generationMessage && (
            <div className="rounded-xl border border-emerald-500/30 bg-emerald-900/20 text-sm text-emerald-100 px-3 py-2">
              {generationMessage}
            </div>
          )}

          {generationMode && (() => {
            const isPhoto = generationMode === 'photo';
            const price = isPhoto ? MEDIA_COSTS.photo : MEDIA_COSTS.video;
            const promptValue = isPhoto ? photoPrompt : videoPrompt;
            const setPrompt = isPhoto ? setPhotoPrompt : setVideoPrompt;
            const generating = isPhoto ? photoGenerating : videoGenerating;
            const placeholder = isPhoto
              ? (language === 'en' ? 'Describe the photo idea, pose, clothes...' : 'Опишите идею фото, позу, одежду...')
              : (language === 'en' ? 'Describe the video motion, camera, vibe...' : 'Опишите движение, камеру и атмосферу...');

            const canGenerate = isPhoto
              ? !!promptValue.trim() && !!photoReference
              : !!promptValue.trim() && !!selectedVideoImage;

            return (
              <div className="rounded-2xl border border-white/15 bg-black/40 p-3 max-h-[60vh] overflow-y-auto text-base sm:text-lg">
                <div className="w-full space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-xl sm:text-2xl font-semibold text-white">
                        {isPhoto
                          ? (language === 'en' ? 'Photo generation' : 'Генерация фото')
                          : (language === 'en' ? 'Video generation' : 'Генерация видео')
                        } · {price} {language === 'en' ? 'tokens' : 'токенов'}
                      </div>
                      <p className="text-xs text-gray-400 mt-1">
                        {language === 'en'
                          ? 'Reference seed and look of this model stay locked, so the face never changes. (Due to high demand, video generation may take up to 5 minutes. Thank you for your patience.)'
                          : 'Сид и внешность модели фиксированы, поэтому лицо всегда остаётся прежним. (Из-за высокой нагрузки генерация видео может занять до 5 минут. Спасибо за ожидание.)'}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={closeGenerationPanel}
                      disabled={generating}
                      className="text-gray-400 hover:text-white text-xl px-2 disabled:opacity-50"
                      aria-label={language === 'en' ? 'Close generation panel' : 'Закрыть панель генерации'}
                    >
                      ×
                    </button>
                  </div>

                  <textarea
                    value={promptValue}
                    onChange={(e) => {
                      setPrompt(e.target.value);
                      // Auto-resize: expand upward, max 5 lines
                      const textarea = e.target;
                      textarea.style.height = 'auto';
                      const lineHeight = 24; // Approximate line height in pixels
                      const maxLines = 5;
                      const maxHeight = lineHeight * maxLines;
                      const scrollHeight = textarea.scrollHeight;
                      const newHeight = Math.min(scrollHeight, maxHeight);
                      textarea.style.height = `${newHeight}px`;
                      textarea.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden';
                    }}
                    placeholder={placeholder}
                    className="w-full rounded-2xl bg-black/30 border border-white/10 text-sm text-white px-4 py-3 min-h-[96px] max-h-[120px] resize-none focus:outline-none focus:ring-2 focus:ring-burgundy-500"
                    style={{ 
                      height: '96px',
                      overflowY: 'hidden'
                    }}
                  />

                  {isPhoto && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="text-xs uppercase tracking-[0.3em] text-gray-400">
                          {language === 'en'
                            ? 'Reference image'
                            : 'Референс-фото'}
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              setPhotoReference(media.images?.[0] || character?.avatar || character?.mainImage || null);
                              setShowPhotoRefPicker(false);
                            }}
                            className="text-xs text-gray-300 hover:text-white underline"
                          >
                            {language === 'en' ? 'Reset' : 'Сбросить'}
                          </button>
                          <button
                            type="button"
                            onClick={() => setShowPhotoRefPicker((v) => !v)}
                            className="text-xs text-burgundy-200 hover:text-white underline"
                          >
                            {language === 'en' ? 'Replace reference image' : 'Заменить референс'}
                          </button>
                        </div>
                      </div>
                      {photoReference ? (
                        <div className="w-full rounded-xl border border-white/10 bg-black/40 overflow-hidden h-48 flex items-center justify-center">
                          <img
                            src={resolveMediaUrl(photoReference)}
                            alt="Reference"
                            className="h-full object-contain"
                          />
                        </div>
                      ) : (
                        <div className="text-sm text-red-300">
                          {language === 'en' ? 'Reference image is required' : 'Нужно выбрать референс'}
                        </div>
                      )}
                      {showPhotoRefPicker && media.images.length > 0 && (
                        <div className="w-full overflow-x-auto pb-1">
                          <div className="flex gap-2">
                            {media.images.map((url) => (
                              <button
                                key={url}
                                type="button"
                                onClick={() => {
                                  setPhotoReference(url);
                                  setShowPhotoRefPicker(false);
                                }}
                                className={`relative flex-shrink-0 rounded-xl border ${
                                  photoReference === url
                                    ? 'border-burgundy-500 ring-2 ring-burgundy-500'
                                    : 'border-white/10'
                                } bg-black/40 overflow-hidden`}
                                style={{ width: 96, height: 144 }}
                              >
                                <img
                                  src={resolveMediaUrl(url)}
                                  alt="Reference option"
                                  className="w-full h-full object-cover"
                                />
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {!isPhoto && media.images.length > 0 && (
                    <div className="space-y-2">
                      <div className="text-xs uppercase tracking-[0.3em] text-gray-400">
                        {language === 'en'
                          ? 'Choose base photo for the video'
                          : 'Выберите базовое фото для видео'}
                      </div>
                      <div className="w-full overflow-x-auto pb-1">
                        <div className="flex gap-2">
                          {media.images.map((url) => (
                            <button
                              key={url}
                              type="button"
                              onClick={() => setSelectedVideoImage(url)}
                              className={`relative flex-shrink-0 rounded-xl border ${
                                selectedVideoImage === url
                                  ? 'border-burgundy-500 ring-2 ring-burgundy-500'
                                  : 'border-white/10'
                              } bg-black/40 overflow-hidden`}
                              style={{ width: 96, height: 144 }}
                            >
                              <img
                                src={resolveMediaUrl(url)}
                                alt="Base frame"
                                className="w-full h-full object-cover"
                              />
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {generationError && (
                    <div className="text-sm text-red-300 bg-red-900/30 border border-red-600/30 px-3 py-2 rounded-xl">
                      {generationError}
                    </div>
                  )}

                  <div className="flex flex-wrap items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setPrompt('')}
                      disabled={generating || !promptValue}
                      className="px-4 py-2 rounded-xl border border-white/20 text-sm text-gray-300 hover:bg-white/10 disabled:opacity-50"
                    >
                      {language === 'en' ? 'Clear' : 'Очистить'}
                    </button>
                    <button
                      type="button"
                      onClick={isPhoto ? handlePhotoGeneration : handleVideoGeneration}
                      disabled={generating || !canGenerate}
                      className="px-5 py-2 rounded-xl bg-burgundy-600 hover:bg-burgundy-500 disabled:opacity-50 text-sm font-semibold text-white"
                    >
                      {generating
                        ? (language === 'en' ? 'Generating...' : 'Генерация...')
                        : (language === 'en' ? 'Generate' : 'Создать')}
                    </button>
                  </div>
                </div>
              </div>
            );
          })()}

          {!generationMode && generationError && (
            <div className="text-sm text-red-300 bg-red-900/30 border border-red-600/30 px-3 py-2 rounded-xl">
              {generationError}
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 items-stretch w-full">
            <div className="relative sm:w-auto" ref={generationMenuRef}>
              <button
                type="button"
                onClick={() => {
                  if (!user) {
                    navigate('/web/register');
                    return;
                  }
                  setGenerationMenuOpen((prev) => !prev);
                }}
                disabled={!user}
                className={`w-full sm:w-auto px-4 py-3 rounded-2xl border-2 border-burgundy-500/70 text-white font-semibold flex items-center justify-center bg-black/40 hover:bg-burgundy-500/10 transition ${!user ? 'opacity-50 cursor-not-allowed' : ''}`}
                aria-label={language === 'en' ? 'Open actions menu' : 'Открыть меню действий'}
                title={!user ? (language === 'en' ? 'Please register to use this feature' : 'Пожалуйста, зарегистрируйтесь для использования этой функции') : undefined}
              >
                <span className="text-lg leading-none">⋯</span>
              </button>
              {generationMenuOpen && (
                <div className="absolute bottom-full mb-2 left-0 w-48 rounded-2xl border border-white/10 bg-black/90 shadow-2xl py-2 z-30">
                  <div className="px-4 pb-2 text-[11px] uppercase tracking-[0.4em] text-gray-500">
                    {language === 'en' ? 'Create' : 'Создать'}
                  </div>
                  <button
                    type="button"
                    onClick={() => openGenerationPanel('photo')}
                    className="w-full text-left px-4 py-3 hover:bg-white/5 transition flex items-center justify-between gap-3"
                  >
                    <span className="text-white font-medium">{language === 'en' ? 'Photo' : 'Фото'}</span>
                    <span className="text-xs text-gray-400">{MEDIA_COSTS.photo} {language === 'en' ? 'tokens' : 'токенов'}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => openGenerationPanel('video')}
                    className="w-full text-left px-4 py-3 hover:bg-white/5 transition flex items-center justify-between gap-3"
                  >
                    <span className="text-white font-medium">{language === 'en' ? 'Video' : 'Видео'}</span>
                    <span className="text-xs text-gray-400">{MEDIA_COSTS.video} {language === 'en' ? 'tokens' : 'токенов'}</span>
                  </button>
                </div>
              )}
            </div>

          <div className="flex-1 max-w-full sm:max-w-[52rem] lg:max-w-[60rem]">
            <textarea
              ref={messageInputRef}
              value={input}
              onChange={(e) => {
                if (!user) return; // Block input for unregistered users
                setInput(e.target.value);
                // Auto-resize: expand upward, max 5 lines
                const textarea = e.target;
                textarea.style.height = 'auto';
                const lineHeight = 24; // Approximate line height in pixels
                const maxLines = 5;
                const maxHeight = lineHeight * maxLines;
                const scrollHeight = textarea.scrollHeight;
                const newHeight = Math.min(scrollHeight, maxHeight);
                textarea.style.height = `${newHeight}px`;
                textarea.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden';
              }}
              onKeyDown={(e) => {
                if (!user) {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    navigate('/web/register');
                  }
                  return;
                }
                // Enter => отправка сообщения, Shift+Enter => новая строка
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage(e);
                }
              }}
              placeholder={!user 
                ? (language === 'en' ? 'Register to send messages...' : 'Зарегистрируйтесь, чтобы отправлять сообщения...')
                : (language === 'en' ? 'Type a message...' : 'Введите сообщение...')}
              disabled={!user}
              className={`w-full px-3 sm:px-4 py-3 bg-transparent text-white placeholder-gray-400 focus:outline-none text-base sm:text-lg border border-white/10 rounded-2xl resize-none ${!user ? 'opacity-50 cursor-not-allowed' : ''}`}
              style={{ 
                touchAction: 'manipulation',
                minHeight: '48px',
                height: '48px',
                overflowY: 'hidden'
              }}
              rows={1}
            />
          </div>
          {!user ? (
            <button
              type="button"
              onClick={() => navigate('/web/register')}
              className="bg-burgundy-600 hover:bg-burgundy-500 text-white font-semibold px-4 sm:px-6 py-3 rounded-2xl transition text-base sm:text-lg"
              style={{ touchAction: 'manipulation' }}
            >
              {language === 'en' ? 'Register to send' : 'Зарегистрироваться'}
            </button>
          ) : (
            <button
              type="submit"
              disabled={loading || !hasTokens}
              onClick={(e) => {
                if (!hasTokens) {
                  e.preventDefault();
                  navigate('/web/promo');
                  return;
                }
              }}
              className="bg-burgundy-600 hover:bg-burgundy-500 disabled:opacity-50 text-white font-semibold px-4 sm:px-6 py-3 rounded-2xl transition text-base sm:text-lg"
              style={{ touchAction: 'manipulation' }}
              title={
                !hasTokens
                  ? (language === 'en' ? 'Top up tokens to send messages' : 'Пополните баланс, чтобы отправлять сообщения')
                  : undefined
              }
            >
              {language === 'en' ? 'Send' : 'Отправить'}
            </button>
          )}
          </div>
        </div>
      </form>
    </section>
  );

  return (
    <>
      {isGroupChat ? (
        <div
          className="fixed inset-x-0 chat-container"
          style={{
            touchAction: 'pan-y',
            display: 'flex',
            flexDirection: 'row',
            top: '64px',
            width: '100vw',
            height: 'calc(100vh - 64px)',
            zIndex: 10
          }}
        >
          <div className="absolute inset-0 flex">
            {(groupData?.participants || [])
              .filter((p) => p.type === 'character')
              .map((p, idx, arr) => (
                <div
                  key={p.key}
                  className="flex-1 relative"
                  style={{
                    backgroundImage: `url(${resolveMediaUrl(p.avatar_url)})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    filter: 'brightness(0.55)'
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/55 to-black/75" />
                  {idx < arr.length - 1 && <div className="absolute right-0 top-0 bottom-0 w-px bg-white/10" />}
                </div>
              ))}
            {(!groupData?.participants || groupData.participants.filter((p) => p.type === 'character').length === 0) && (
              <div className="flex-1 bg-gradient-to-br from-black/90 via-black/80 to-burgundy-900" />
            )}
          </div>
          <div className="relative z-10 flex h-full w-full flex-row">
            {renderSidebar()}
            <section className="flex-1 flex flex-col px-3 sm:px-6 py-4 gap-4 overflow-hidden">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-semibold text-white drop-shadow">
                    {language === 'en' ? 'Group chat' : 'Групповой чат'}
                  </h1>
                  <p className="text-gray-200 text-sm mt-1">
                    {language === 'en' ? 'Carousel: choose who speaks next' : 'Карусель: задайте порядок сообщений'}
                  </p>
                  {groupData?.current_turn && (
                    <div className="text-sm text-burgundy-200 mt-1">
                      {language === 'en' ? 'Current turn:' : 'Текущий ход:'}{' '}
                      {groupData.participants?.find((p) => p.key === groupData.current_turn)?.name || groupData.current_turn}
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-black/55 border border-white/10 rounded-2xl p-3 backdrop-blur">
                <div className="flex flex-wrap gap-3">
                  {(groupData?.participants || []).map((p) => (
                    <div
                      key={p.key}
                      className={`flex items-center gap-2 px-3 py-2 rounded-xl border ${p.is_current_turn ? 'border-burgundy-500 bg-burgundy-900/30' : 'border-white/10 bg-black/30'}`}
                    >
                      <div className="h-10 w-10 rounded-full overflow-hidden bg-dark-700 border border-white/10 flex items-center justify-center">
                        {p.avatar_url ? (
                          <img src={p.avatar_url} alt={p.name} className="h-full w-full object-cover" />
                        ) : (
                          <span className="text-sm text-gray-300">{p.name?.charAt(0)?.toUpperCase()}</span>
                        )}
                      </div>
                      <div>
                        <div className="text-sm font-semibold">{p.name}</div>
                        <div className="text-xs text-gray-400">
                          {p.type === 'user' ? (language === 'en' ? 'User' : 'Пользователь') : (language === 'en' ? 'Model' : 'Модель')}
                        </div>
                      </div>
                      {p.type === 'character' && (
                        <button
                          type="button"
                          onClick={() => handleForceGroup(p.key)}
                          disabled={groupForcing}
                          className="ml-2 text-xs text-burgundy-200 hover:text-white underline"
                        >
                          {language === 'en' ? 'Force reply' : 'Заставить ответить'}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex-1 min-h-[320px] bg-black/55 border border-white/10 rounded-2xl p-4 backdrop-blur overflow-y-auto space-y-4">
                {groupDataLoading ? (
                  <div className="text-gray-300 text-center">
                    {language === 'en' ? 'Loading group chat...' : 'Загрузка группового чата...'}
                  </div>
                ) : groupDataError ? (
                  <div className="text-red-300 text-center">{groupDataError}</div>
                ) : !groupData?.messages?.length ? (
                  <div className="text-gray-400 text-center">
                    {language === 'en' ? 'No messages yet.' : 'Сообщений пока нет.'}
                  </div>
                ) : (
                  groupData.messages.map((m) => (
                    <div
                      key={m.id}
                      className={`rounded-2xl px-4 py-3 border ${m.sender_type === 'user' ? 'bg-burgundy-900/40 border-burgundy-500/40' : 'bg-black/40 border-white/10'}`}
                    >
                      <div className="flex items-center justify-between text-sm text-gray-300 mb-1">
                        <span className="font-semibold">{m.sender_name || m.sender_ref}</span>
                        {m.created_at && (
                          <span className="text-xs text-gray-500">{new Date(m.created_at).toLocaleString()}</span>
                        )}
                      </div>
                      <p className="text-white whitespace-pre-line">{m.content}</p>
                    </div>
                  ))
                )}
              </div>

              <form onSubmit={handleGroupSend} className="space-y-3">
                {groupDataError && (
                  <div className="text-sm text-red-300 bg-red-900/30 border border-red-600/30 px-3 py-2 rounded-xl">
                    {groupDataError}
                  </div>
                )}
                <textarea
                  value={groupInput}
                  onChange={(e) => setGroupInput(e.target.value)}
                  rows={3}
                  className="w-full rounded-2xl bg-black/70 border border-white/10 text-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-burgundy-500 backdrop-blur"
                  placeholder={language === 'en' ? 'Type your message...' : 'Введите сообщение...'}
                />
                <div className="flex items-center justify-end">
                  <button
                    type="submit"
                    disabled={groupSending || !groupInput.trim() || !user}
                    className="px-5 py-2 rounded-xl bg-burgundy-600 hover:bg-burgundy-500 text-white font-semibold disabled:opacity-50"
                  >
                    {groupSending ? (language === 'en' ? 'Sending...' : 'Отправка...') : (language === 'en' ? 'Send' : 'Отправить')}
                  </button>
                </div>
              </form>
            </section>

            <aside className={`${rightCollapsed ? 'w-14' : 'w-72'} transition-all duration-200 bg-black/50 border-l border-white/10 backdrop-blur-lg flex flex-col`}>
              <div className="flex items-center justify-between px-3 py-3 border-b border-white/10">
                {!rightCollapsed && (
                  <div className="text-sm font-semibold">
                    {language === 'en' ? 'Models' : 'Модели'}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => setRightCollapsed((v) => !v)}
                  className="text-lg text-gray-300 hover:text-white"
                  aria-label="Toggle models"
                >
                  {rightCollapsed ? '‹' : '›'}
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-3 space-y-3">
                {(groupData?.participants || []).filter((p) => p.type === 'character').length === 0 ? (
                  <div className="text-gray-400 text-sm">
                    {language === 'en' ? 'No models' : 'Модели отсутствуют'}
                  </div>
                ) : (
                  (groupData?.participants || []).filter((p) => p.type === 'character').map((p) => (
                    <div key={p.key} className="rounded-xl border border-white/10 bg-black/30 p-3">
                      <div className="flex items-center gap-3">
                        <img
                          src={resolveMediaUrl(p.avatar_url)}
                          alt={p.name}
                          className="h-12 w-12 rounded-xl object-cover border border-white/10"
                        />
                        <div>
                          <div className="font-semibold text-white text-sm">{p.name}</div>
                          <div className="text-xs text-gray-400">{language === 'en' ? 'Model' : 'Модель'}</div>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => navigate(`/web/chat/${p.character_id}`)}
                        className="mt-3 w-full px-3 py-2 rounded-lg bg-burgundy-600 hover:bg-burgundy-500 text-white text-sm font-semibold"
                      >
                        {language === 'en' ? 'Open media' : 'Открыть медиа'}
                      </button>
                    </div>
                  ))
                )}
              </div>
            </aside>
          </div>
        </div>
      ) : (
        <div 
          className="fixed inset-x-0 chat-container"
          style={{
            touchAction: 'pan-y', // Разрешаем вертикальный скролл, предотвращаем зум
            display: 'flex',
            flexDirection: 'row',
            top: '64px', // под навбар
            width: '100vw',
            height: 'calc(100vh - 64px)',
            zIndex: 10
          }}
        >
          <div className="absolute inset-0 bg-black overflow-hidden">
            <img
              src={resolveMediaUrl(character?.mainImage || character?.avatar || '/webava.png')}
              alt={character?.name || 'Background'}
              className="w-full object-cover"
              style={{ 
                objectPosition: 'center top',
                position: 'absolute',
                top: '-40%',
                left: 0,
                width: '100%',
                height: '140%'
              }}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-br from-black/90 via-black/80 to-burgundy-950/70" />
          </div>
          <div className="relative z-10 flex h-full w-full flex-row">
            {renderSidebar()}
            {renderChatArea()}
            {renderMediaPanel()}
          </div>
        </div>
      )}

      {activeMedia && (() => {
        const list = media[activeMedia.collection] || [];
        const currentSrc = activeMedia.directUrl || list[activeMedia.index];
        if (!currentSrc) {
          return null;
        }
        const total = activeMedia.directUrl ? 1 : list.length;
        const counterLabel = total > 1 ? `${activeMedia.index + 1} / ${total}` : '';
        return (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center p-6" style={{ zIndex: 2000 }}>
          <button
            type="button"
            className="absolute top-6 right-6 text-white text-2xl"
            onClick={() => setActiveMedia(null)}
          >
            ×
          </button>
            {total > 1 && (
              <>
                <button
                  type="button"
                  onClick={() => handleMediaNavigation(-1)}
                  className="absolute left-6 top-1/2 -translate-y-1/2 text-white text-4xl flex items-center justify-center hover:opacity-80 transition opacity-70"
                  aria-label={language === 'en' ? 'Previous media' : 'Предыдущее медиа'}
                >
                  ‹
                </button>
                <button
                  type="button"
                  onClick={() => handleMediaNavigation(1)}
                  className="absolute right-6 top-1/2 -translate-y-1/2 text-white text-4xl flex items-center justify-center hover:opacity-80 transition opacity-70"
                  aria-label={language === 'en' ? 'Next media' : 'Следующее медиа'}
                >
                  ›
                </button>
              </>
            )}
            <div className="absolute bottom-6 text-sm text-white/70 tracking-widest">
              {counterLabel}
            </div>
          <div className="flex items-center justify-center w-full h-full">
            {activeMedia.type === 'video' ? (
                <video 
                  src={resolveMediaUrl(currentSrc)} 
                  controls 
                  autoPlay 
                  className="max-w-full max-h-[90vh] w-auto h-auto"
                  style={{ objectFit: 'contain' }}
                />
            ) : (
                <img 
                  src={resolveMediaUrl(currentSrc)} 
                  alt="Media" 
                  className="max-w-full max-h-[90vh] w-auto h-auto"
                  style={{ objectFit: 'contain' }}
                />
            )}
          </div>
        </div>
        );
      })()}

      {renderGalleryOverlay()}
    </>
  );
}

export default Chat;
